import { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DialogContent } from "@/components/ui/dialog";
import { useVisualViewport } from "@/hooks/useVisualViewport";
import { mobileSheetGeometry } from "@/lib/visualViewport";
import { cn } from "@/lib/utils";

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
>;

/** Fields that bring up the on-screen keyboard. */
const TEXT_ENTRY_SELECTOR =
  "textarea, [contenteditable='true'], " +
  "input:not([type='checkbox']):not([type='radio']):not([type='button'])" +
  ":not([type='submit']):not([type='reset']):not([type='file'])" +
  ":not([type='range']):not([type='color'])";

// A DialogContent that is bordered and centered on desktop, but becomes
// a full-screen sheet on mobile (< sm) that tracks the visual viewport, so
// the on-screen keyboard never hides the field being typed in or the footer.
export function ResponsiveDialogContent({
  className,
  style,
  children,
  ...props
}: DialogContentProps) {
  const viewport = useVisualViewport();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 640);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Safari scrolls the focused field into view before the keyboard has
  // finished opening, against the full-height sheet. Once the sheet has
  // shrunk to the space left above the keyboard, the field can sit below the
  // fold of its scroll area: bring it back before the frame is painted.
  useLayoutEffect(() => {
    if (!isMobile) return;
    const field = document.activeElement;
    if (
      field instanceof HTMLElement &&
      contentRef.current?.contains(field) &&
      field.matches(TEXT_ENTRY_SELECTOR)
    ) {
      field.scrollIntoView({ block: "nearest" });
    }
  }, [isMobile, viewport.height]);

  const sheet = isMobile
    ? mobileSheetGeometry(viewport, window.innerHeight)
    : null;

  return (
    <DialogContent
      {...props}
      ref={contentRef}
      className={cn(
        // The built-in close control stays: it is the single dismiss
        // affordance in the header, and consumers render no other one.
        "p-0 gap-0 flex flex-col w-full max-w-2xl",
        // On mobile the sheet's top follows the visual viewport (--sheet-top):
        // when Safari pans the page to reveal a field above the keyboard, a
        // sheet pinned to top: 0 would slide its header and field off screen.
        "max-sm:!inset-x-0 max-sm:!top-[var(--sheet-top,0px)] max-sm:!translate-x-0 max-sm:!translate-y-0",
        "max-sm:!max-w-none max-sm:!w-full max-sm:!rounded-none max-sm:!border-0",
        // Full-screen sheets sit under the iOS home indicator: keep the footer
        // above it (the app viewport is `viewport-fit=cover`).
        "max-sm:pb-[env(safe-area-inset-bottom)]",
        className,
      )}
      style={{
        ...(sheet
          ? ({
              "--sheet-top": `${sheet.top}px`,
              height: `${sheet.height}px`,
              maxHeight: `${sheet.height}px`,
              // With the keyboard up, the home indicator is under the keyboard.
              ...(sheet.keyboardOpen ? { paddingBottom: 0 } : {}),
            } as React.CSSProperties)
          : { maxHeight: `${viewport.height * 0.9}px` }),
        ...style,
      }}
    >
      {children}
    </DialogContent>
  );
}
